import { useCallback, useState, useEffect, useRef } from 'react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { quickStartApiRef, UserPreferences } from '../api';

const SKIP_SESSION_KEY = 'quickstart-skipped-session';

// Check if skipped this session (persists across page refreshes within same browser session)
function isSkippedThisSession(): boolean {
  try {
    return sessionStorage.getItem(SKIP_SESSION_KEY) === 'true';
  } catch {
    return false;
  }
}

// Mark as skipped for this session
function setSkippedThisSession(): void {
  try {
    sessionStorage.setItem(SKIP_SESSION_KEY, 'true');
  } catch {
    // Ignore storage errors
  }
}

// Clear skip flag (for reset)
function clearSkippedThisSession(): void {
  try {
    sessionStorage.removeItem(SKIP_SESSION_KEY);
  } catch {
    // Ignore storage errors
  }
}

interface QuickStartStatus {
  isFirstTimeUser: boolean;
  isLoading: boolean;
  isGuest: boolean;
  savedPreferences: UserPreferences | null;
  savePreferences: (preferences: UserPreferences) => Promise<void>;
  completeQuickStart: () => Promise<boolean>;
  skipQuickStart: () => void;
  resetQuickStart: () => Promise<void>;
}

/**
 * Check if the user is a guest based on their entity ref.
 * Guest users typically have refs like "user:development/guest" or "user:default/guest".
 */
function isGuestUser(userEntityRef: string | undefined): boolean {
  if (!userEntityRef) return false;
  // Guest users have "guest" as their name
  return userEntityRef.toLowerCase().endsWith('/guest');
}

/**
 * Hook to track whether a user has completed the quick start wizard.
 * Status is stored in the database via the quick-start backend plugin.
 * Guest users are automatically skipped (not considered first-time users).
 */
export const useQuickStartStatus = (): QuickStartStatus => {
  const quickStartApi = useApi(quickStartApiRef);
  const identityApi = useApi(identityApiRef);
  const skippedSession = isSkippedThisSession();
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(!skippedSession);
  const [isLoading, setIsLoading] = useState(!skippedSession);
  const [isGuest, setIsGuest] = useState(false);
  const [savedPreferences, setSavedPreferences] =
    useState<UserPreferences | null>(null);
  const checkedRef = useRef(false);

  // Check status on mount
  useEffect(() => {
    // If already skipped this session, don't check again
    if (isSkippedThisSession()) {
      setIsFirstTimeUser(false);
      setIsLoading(false);
      return;
    }

    // Prevent duplicate checks
    if (checkedRef.current) return;
    checkedRef.current = true;

    let cancelled = false;

    const checkStatus = async () => {
      try {
        // First check if user is a guest
        const identity = await identityApi.getBackstageIdentity();
        const guestUser = isGuestUser(identity?.userEntityRef);

        if (!cancelled) {
          setIsGuest(guestUser);
        }

        // Skip API call for guest users - they don't need quick start
        if (guestUser) {
          if (!cancelled) {
            setIsFirstTimeUser(false);
            setIsLoading(false);
          }
          return;
        }

        const status = await quickStartApi.getStatus();
        if (!cancelled) {
          setIsFirstTimeUser(!status.completed);
          setSavedPreferences({
            position: status.position,
            idpExperience: status.idpExperience,
          });
          setIsLoading(false);
        }
      } catch (error) {
        // On error (e.g., not logged in yet), assume first time user
        if (!cancelled) {
          setIsFirstTimeUser(true);
          setIsLoading(false);
        }
      }
    };

    checkStatus();

    return () => {
      cancelled = true;
    };
  }, [quickStartApi, identityApi]);

  const savePreferences = useCallback(
    async (preferences: UserPreferences) => {
      // Skip for guest users
      if (isGuest) return;

      try {
        const status = await quickStartApi.savePreferences(preferences);
        setSavedPreferences({
          position: status.position,
          idpExperience: status.idpExperience,
        });
      } catch (error) {
        console.error('Failed to save preferences:', error);
      }
    },
    [quickStartApi, isGuest],
  );

  const completeQuickStart = useCallback(async (): Promise<boolean> => {
    // Skip for guest users - always succeed
    if (isGuest) {
      setSkippedThisSession();
      setIsFirstTimeUser(false);
      return true;
    }

    try {
      await quickStartApi.complete();
      setIsFirstTimeUser(false);
      return true;
    } catch (error) {
      console.error('Failed to save quick start completion status:', error);
      return false;
    }
  }, [quickStartApi, isGuest]);

  // Skip quick start for this session only (will show again on next login/new browser session)
  const skipQuickStart = useCallback(() => {
    setSkippedThisSession();
    setIsFirstTimeUser(false);
  }, []);

  const resetQuickStart = useCallback(async () => {
    // Skip for guest users
    if (isGuest) return;

    // Also reset session skip flag
    clearSkippedThisSession();

    try {
      await quickStartApi.reset();
      setIsFirstTimeUser(true);
      setSavedPreferences(null);
    } catch (error) {
      console.error('Failed to reset quick start status:', error);
    }
  }, [quickStartApi, isGuest]);

  return {
    isFirstTimeUser,
    isLoading,
    isGuest,
    savedPreferences,
    savePreferences,
    completeQuickStart,
    skipQuickStart,
    resetQuickStart,
  };
};
